/*
 * MainFrameRetailGID.js:
 * This is the main UI container that includes layout, states and controls.
 * It is divided into 4 sections (divs) that are displayed based on the app's progress.
 * The transitions from one section to another is based on user interaction or API fetch state.
 */
import React, { useState } from "react";

import Div1InputGID from "./Div1InputEmail/Div1InputGID";
import Div2Searching from "./Div2UserInfo/Div2Searching";
import Div2Error from "./Div2UserInfo/Div2Error";
import Div2GIDFound from "./Div2UserInfo/Div2GIDFound";
import useSearchUser from "../hooks/useSearchUser";
import { useApiActivity } from "../contexts/ApiActivityContext";

import "./MainFrame.css";
import { unlockGID } from "../api/users";

const MainFrameRetailGID = ({ hasRetailGidGroup }) => {
  let usernameType = "email";
  console.log("MainFrameRetailGID.js: hasRetailGidGroup", hasRetailGidGroup);
  if (hasRetailGidGroup) {
    usernameType = "retailGID";
  }

  const { isActive, beginActivity, endActivity } = useApiActivity();

  const [activeStep, setActiveStep] = useState(1);
  const [enteredEmail, setEnteredEmail] = useState("");

  const [isDiv2Visible, setDiv2Visible] = useState(false);
  const [div2FetchStatus, setDiv2FetchStatus] = useState("");
  const [div2Title, setDiv2Title] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [div2Error, setDiv2Error] = useState("");

  const { searchUser } = useSearchUser(
    setActiveStep,
    setDiv2Visible,
    setDiv2FetchStatus,
    setDiv2Title,
    setUserInfo,
    setDiv2Error
  );

  const resetState = () => {
    console.log("MainFrame: resetState: Resetting states...");
    setActiveStep(1);
    setEnteredEmail("");

    setDiv2Visible(false);
    setDiv2FetchStatus("");
    setDiv2Title("");
    setUserInfo({});
    setDiv2Error("");
  };

  const Div1SearchButtonClicked = async () => {
    await searchUser(enteredEmail, usernameType);
  };

  const handleUnlockAccountButtonPress = async () => {
    setDiv2Title("Unlocking Account...");
    beginActivity("retail-gid");

    try {
      const response = await unlockGID(userInfo.user.activeDirectory.username);
      if (response.errorMsg) {
        setDiv2Title("Error Unlocking Account");
        setDiv2Error(response.errorMsg);
        return;
      }

      const message =
        "Account unlocked successfully. Please allow up to 15 minutes for Active Directory to sync globally.";
      setDiv2Title(message);
      setDiv2Error("");
      setUserInfo((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          status: "ACTIVE",
          activeDirectory: {
            ...prev.user.activeDirectory,
            locked: false,
          },
        },
      }));
    } catch (error) {
      setDiv2Title("Error Unlocking Account");
      setDiv2Error(`Failed to unlock account: ${error}`);
    } finally {
      endActivity("retail-gid");
    }
  };

  const Div2StartOverClicked = () => {
    resetState();
  };

  if (hasRetailGidGroup === false) {
    return (
      <div className="main-wrapper">
        <h1 className="text-color-red">
          You do not have access to this application.
        </h1>
        <p className="text-color-red">
          Please contact your manager or the EIAM Operations team for
          assistance.
        </p>
      </div>
    );
  }

  return (
    <div className="main-wrapper">
      <Div1InputGID
        enteredEmail={enteredEmail}
        setEnteredEmail={setEnteredEmail}
        Div1SearchButtonClicked={Div1SearchButtonClicked}
        setUserInfo={setUserInfo}
        setDiv2Visible={setDiv2Visible}
        activeStep={activeStep}
      />

      {isDiv2Visible && div2FetchStatus === "searching" && (
        <Div2Searching div2Title={div2Title} activeStep={activeStep} />
      )}

      {isDiv2Visible && div2FetchStatus === "data" && (
        <Div2GIDFound
          div2Title={div2Title}
          userInfo={userInfo.user}
          activeStep={activeStep}
          handleUnlockAccountButtonPress={handleUnlockAccountButtonPress}
        />
      )}

      {isDiv2Visible && div2FetchStatus === "error" && (
        <Div2Error
          div2Title={div2Title}
          errorMsg={div2Error}
          Div2StartOverClicked={Div2StartOverClicked}
          activeStep={activeStep}
        />
      )}
    </div>
  );
};

export default MainFrameRetailGID;
